/**
 * azure-functions/shared/state/migrateState.cjs
 *
 * Canonical (GH #7115 pattern): shared by the browser, which re-exports it from src/, and the Function App, so the server can run the exact load path the browser runs (measurement-parity-diff). Edit this file, never the src/ re-export.
 *
 * One-way migration: legacy framework control IDs → TCF canonical IDs.
 * Runs transparently on tenant state load for tenants that still have
 * old ISO 27001 clause IDs, ISO 27002 Annex A IDs, or CIS18 safeguard IDs.
 *
 * Safe to run on already-migrated state — TCF IDs pass through unchanged.
 */

'use strict';

const {
  migrateAdoptedToObligations,
  migrateTaskEvidence,
  migrateAdhocTasks,
  migrateRiskActions,
} = require('./obligationMigration.cjs');
const { migrateEvidenceRequests } = require('./evidenceRequestMigration.cjs');
const { backfillDocumentNumbers } = require('../documents/documentNumbering.cjs');
const {
  LEGACY_TO_SCF,
  migrateControlId,
  migrateControlIds,
} = require('../compliance/legacyToScf.cjs');
// ^ The legacy → TCF id table and the two id migrators are the compliance
// engine's (evaluateControl normalises every control id through them), so
// their one definition lives with it (GH #7115). Re-exported below.

/**
 * Migrate an entire tenant state snapshot.
 * Walks documents[].controls, controlOverrides keys, and legacy tasks[].
 * After migration, state.tasks is emptied — all tasks are distributed to
 * enabledObligations, adhocTasks, and risk.actions[].
 * Returns a new state object — does not mutate the input.
 */
function migrateState(state) {
  if (!state) return state;

  // Document controls must NOT use migrateControlIds() because it deduplicates
  // by TCF ID.  Multiple distinct framework controls (e.g. 6.1.1, 6.1.2, 6.1.3,
  // 8.2, 8.3) can map to the same TCF ID (SCF-RSK-001) — deduplication silently
  // drops all but the first, destroying the user's annotations.  The document
  // annotation system (ControlTagPanel, FrameworkLinkPanel, DOM spans) works with
  // native framework IDs and already handles TCF IDs via the scfToNativeCtrl
  // reverse map, so migration is unnecessary.
  const documents = state.documents || [];

  // Migrate control IDs on legacy tasks before distributing them
  const tasks = (state.tasks || []).map((task) => ({
    ...task,
    controls: migrateControlIds(task.controls),
  }));

  const controlOverrides = {};
  for (const [key, value] of Object.entries(state.controlOverrides || {})) {
    const newKey = migrateControlId(key);
    controlOverrides[newKey] = value;
  }

  // Migrate control IDs and deduplicate by id (last occurrence wins).
  // Dedup guards against duplicates that may have been written by an earlier
  // super-admin push that didn't check for existing entries.
  const controlTopicsMap = new Map();
  for (const topic of state.controlTopics || []) {
    controlTopicsMap.set(topic.id, {
      ...topic,
      controlIds: migrateControlIds(topic.controlIds || []),
    });
  }
  const controlTopics = Array.from(controlTopicsMap.values());

  // Assign sortOrder to any framework missing it (preserves existing array order for live tenants)
  // Backfill type + jurisdiction for known law-type frameworks
  const LAW_FRAMEWORKS = {
    'fw-nis2': { type: 'law', jurisdiction: 'EU' },
    'fw-gdpr': { type: 'law', jurisdiction: 'EU' },
  };
  const frameworks = (state.frameworks || []).map((fw, i) => {
    let updated = fw.sortOrder !== undefined ? fw : { ...fw, sortOrder: i };
    const lawMeta = LAW_FRAMEWORKS[fw.id];
    if (lawMeta && !fw.type) updated = { ...updated, ...lawMeta };
    return updated;
  });

  let migrated = { ...state, documents, tasks, controlOverrides, controlTopics, frameworks };
  migrated = migrateAdoptedToObligations(migrated);
  migrated = migrateTaskEvidence(migrated);
  migrated = migrateAdhocTasks(migrated);
  migrated = migrateRiskActions(migrated);
  migrated = migrateEvidenceRequests(migrated);
  migrated = migrateDocumentNumbers(migrated);

  // After migration, clear legacy tasks — all have been distributed
  if (migrated.tasks && migrated.tasks.length > 0) {
    migrated = { ...migrated, tasks: [] };
  }

  return migrated;
}

// Assign document numbers (TYPE-###) to legacy documents that don't have one.
// Idempotent — re-running on a state where every doc already has a number is
// a no-op except for re-seeding the persistent highwater registry.
function migrateDocumentNumbers(state) {
  const docs = state.documents || [];
  if (!docs.length && !(state.deletedDocuments || []).length) return state;
  const result = backfillDocumentNumbers(
    docs,
    state.documentTypes || [],
    state.documentNumberSequences || {},
    state.deletedDocuments || []
  );
  if (result.documents === docs && result.sequenceRegistry === state.documentNumberSequences) {
    return state;
  }
  return {
    ...state,
    documents: result.documents,
    documentNumberSequences: result.sequenceRegistry,
  };
}

module.exports = {
  migrateState,
  migrateControlId,
  migrateControlIds,
  LEGACY_TO_SCF,
};
